import React from 'react';
import { motion } from 'framer-motion';
import { UserRole } from '../contexts/UserPreferencesContext';

interface RoleSelectorProps {
  selectedRole: UserRole;
  onSelect: (role: UserRole) => void;
}

interface RoleOption {
  id: UserRole;
  title: string;
  description: string;
  icon: string;
  color: string;
  hoverColor: string;
  selectedColor: string;
}

const roleOptions: RoleOption[] = [
  {
    id: 'tech',
    title: 'Technical Leader',
    description: 'CTO, CISO, VP Engineering, or Security Architect. Focus on technical implementation and security controls.',
    icon: '💻',
    color: 'from-blue-500/10 to-cyan-500/10',
    hoverColor: 'hover:border-blue-400/50',
    selectedColor: 'border-blue-500 bg-gradient-to-br from-blue-500/20 to-cyan-500/20',
  },
  {
    id: 'exec',
    title: 'Executive',
    description: 'CEO, CFO, COO, or Board Member. Focus on business risk, compliance, and strategic decisions.',
    icon: '👔',
    color: 'from-purple-500/10 to-violet-500/10',
    hoverColor: 'hover:border-purple-400/50',
    selectedColor: 'border-purple-500 bg-gradient-to-br from-purple-500/20 to-violet-500/20',
  },
];

export const RoleSelector: React.FC<RoleSelectorProps> = ({ selectedRole, onSelect }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
      {roleOptions.map((role, index) => {
        const isSelected = selectedRole === role.id;

        return (
          <motion.button
            key={role.id}
            onClick={() => onSelect(role.id)}
            className={`
              relative p-6 md:p-8 rounded-2xl border-2 text-left
              transition-all duration-300
              ${isSelected
                ? role.selectedColor
                : `border-slate-200 dark:border-aurora-violet-500/20 bg-white/80 dark:bg-dark-elevated/50 ${role.hoverColor}`
              }
              focus:outline-none focus-visible:ring-2 focus-visible:ring-aurora-violet-500/50
            `}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1, duration: 0.3 }}
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
          >
            {/* Selection indicator */}
            {isSelected && (
              <motion.div
                className="absolute top-4 right-4"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              >
                <div className="w-6 h-6 rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500 flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </motion.div>
            )}

            {/* Icon */}
            <div className="text-4xl md:text-5xl mb-4">{role.icon}</div>

            {/* Title */}
            <h3 className="text-xl md:text-2xl font-semibold text-slate-900 dark:text-white mb-2">
              {role.title}
            </h3>

            {/* Description */}
            <p className="text-sm md:text-base text-slate-600 dark:text-slate-300 leading-relaxed">
              {role.description}
            </p>

            {/* Decorative gradient overlay */}
            <div
              className={`
                absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300
                bg-gradient-to-br ${role.color}
                ${!isSelected ? 'group-hover:opacity-100' : ''}
              `}
              style={{ pointerEvents: 'none' }}
            />
          </motion.button>
        );
      })}
    </div>
  );
};

export default RoleSelector;
